import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  return (
    <div
      data-testid="privacy-policy-page"
      className="min-h-screen"
      style={{ backgroundColor: "var(--blush)" }}
    >
      {/* Header */}
      <header className="px-6 py-6 max-w-3xl mx-auto">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-2"
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "0.875rem",
            color: "var(--charcoal-light)",
            textDecoration: "none",
            transition: "color 0.2s",
          }}
        >
          <ArrowLeft size={16} /> Back to Depth Co.
        </Link>
      </header>

      {/* Content */}
      <main className="px-6 pb-24 max-w-3xl mx-auto">
        <h1
          data-testid="privacy-heading"
          style={{
            fontFamily: "var(--font-serif)",
            fontSize: "clamp(2rem, 4vw, 2.75rem)",
            fontWeight: 600,
            color: "var(--charcoal)",
            marginBottom: "0.5rem",
            letterSpacing: "-0.01em",
          }}
        >
          Privacy Policy
        </h1>

        <p
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "0.875rem",
            color: "var(--charcoal-light)",
            marginBottom: "3rem",
          }}
        >
          Last updated: April 2025
        </p>

        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "1rem",
            lineHeight: 1.8,
            color: "var(--charcoal)",
          }}
        >
          <Section title="The short version">
            <p>
              We collect only what we need to make our products work for you.
              We don't sell your data to anyone. Ever. Your trust matters more
              to us than any business model that trades on it.
            </p>
          </Section>

          <Section title="What we collect">
            <p>When you use Resonance or other Depth Co. products, we may collect:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Your name and email address (when you create an account or join our waitlist)</li>
              <li>Profile photos (if you choose to upload one)</li>
              <li>Your answers to reflection prompts and questions within the app</li>
              <li>Basic usage data to help us improve the experience</li>
            </ul>
          </Section>

          <Section title="How we use it">
            <p>Your information is used for:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>
                <strong>Matching and connections</strong> — In Resonance, your
                reflection answers help us suggest meaningful connections.
                Your words are what make the matching work.
              </li>
              <li>
                <strong>App functionality</strong> — To provide, maintain, and
                improve the features you use.
              </li>
              <li>
                <strong>Communication</strong> — To send you updates about
                products you've signed up for. We keep these to a minimum.
              </li>
            </ul>
          </Section>

          <Section title="What we don't do">
            <p>
              We do not sell, rent, or share your personal data with third
              parties for advertising or marketing purposes. Your data stays
              with us and is used solely to power the products you've chosen
              to use.
            </p>
          </Section>

          <Section title="Camera and photo permissions">
            <p>
              If you grant camera access, it's used only for uploading a
              profile photo. We don't access your camera for any other
              purpose, and we don't store or analyse photos beyond what you
              explicitly upload.
            </p>
          </Section>

          <Section title="Data storage and security">
            <p>
              Your data is stored securely using industry-standard encryption
              and infrastructure. We take reasonable precautions to protect
              your information, though no system is perfectly secure.
            </p>
          </Section>

          <Section title="Your choices">
            <p>
              You can request to see, update, or delete your personal data at
              any time by emailing us. If you'd like to be removed from the
              waitlist, just let us know.
            </p>
          </Section>

          <Section title="Get in touch" last>
            <p>
              If you have questions about this policy or how we handle your
              data, reach out to us at{" "}
              <a
                href="mailto:hello@depthco.app"
                data-testid="privacy-contact-email"
                style={{ color: "var(--coral)", textDecoration: "none", fontWeight: 500 }}
              >
                hello@depthco.app
              </a>
              . We're real people and we'll reply personally.
            </p>
          </Section>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children, last = false }) {
  return (
    <div style={{ marginBottom: last ? 0 : "2.5rem" }}>
      <h2
        style={{
          fontFamily: "var(--font-serif)",
          fontSize: "1.35rem",
          fontWeight: 600,
          color: "var(--charcoal)",
          marginBottom: "0.75rem",
        }}
      >
        {title}
      </h2>
      {children}
    </div>
  );
}
